/**
 * Inline chat asset card — the "RESEARCH ASSISTANT OBJECT CARDS" spec
 * (Figma node 1491-64018): white card with a 3px brand-primary left rail,
 * one row of kind icon + kind label + status chip, the asset name, and a
 * timestamp / "View details ›" footer.
 *
 * Presentational only: the status label is computed by the per-kind
 * behavior hooks in MessageBubble (asset-status queries), and the chip
 * itself is the shared {@link ArtifactStatusChip} so the chat card and the
 * detail-page headers can't diverge on status colors.
 */

import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FunctionsIcon from '@mui/icons-material/Functions';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import type { SvgIconProps } from '@mui/material';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import type { ComponentType } from 'react';
import { useIntl } from 'react-intl';

import { ArtifactStatusChip } from '@verantos-dev/shared-components';
import { borderRadius, shadows } from '@verantos-dev/shared-ui-components/tokens';

import type { EpiResourceKind } from '../types/epi-chat-types';

/** Kind icon + sentence-case label, per the object-cards spec. `concept_group`
 *  uses the same `{}` glyph as `ARTIFACT_KIND_ICON` in the project artifact
 *  list, so a concept group reads identically in chat and on the list. */
const KIND_META: Record<EpiResourceKind, { icon: ComponentType<SvgIconProps>; label: string }> = {
  cohort: { icon: PeopleAltOutlinedIcon, label: 'Cohort' },
  analytical_variable: { icon: FunctionsIcon, label: 'Variable' },
  concept_group: { icon: DataObjectIcon, label: 'Concept group' },
  analysis: { icon: AnalyticsOutlinedIcon, label: 'Analysis' },
};

export interface AssetCardProps {
  testId: string;
  ariaLabel: string;
  kind: EpiResourceKind;
  /** Asset display name, e.g. "Sex is Male". */
  name: string;
  /** Unified asset status (Draft / Processing / Ready / Error). */
  statusLabel: string;
  /** ISO timestamp rendered as "Jun 26, 2026 | 10:00:25 AM". */
  createdAt: string;
  disabled?: boolean;
  onClick: () => void;
}

export const AssetCard = ({
  testId,
  ariaLabel,
  kind,
  name,
  statusLabel,
  createdAt,
  disabled = false,
  onClick,
}: AssetCardProps) => {
  const intl = useIntl();
  const { icon: KindIcon, label: kindLabel } = KIND_META[kind];
  const created = new Date(createdAt);
  const timestamp = `${intl.formatDate(created, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })} | ${intl.formatTime(created, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;

  return (
    <Box sx={{ marginY: 2.5, marginX: { xs: 0, sm: 2 } }}>
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        aria-label={ariaLabel}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: '100%',
          gap: 2,
          padding: 4,
          textAlign: 'left',
          backgroundColor: 'background.paper',
          borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
          borderRadius: `${borderRadius.sm}px`,
          boxShadow: shadows.sm,
          opacity: disabled ? 0.72 : 1,
          transition: 'box-shadow 0.15s',
          '&:hover': disabled ? undefined : { boxShadow: shadows.md },
          '&:focus-visible': {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={4}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <KindIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} aria-hidden />
            <Typography variant="body2" color="text.secondary" noWrap>
              {kindLabel}
            </Typography>
          </Stack>
          <ArtifactStatusChip status={statusLabel} data-testid={`${testId}-status-chip`} />
        </Stack>
        <Typography
          variant="body1"
          sx={{ fontWeight: 500, color: 'text.primary', minWidth: 0 }}
          noWrap
        >
          {name}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" sx={{ color: 'text.primary' }} noWrap>
            {timestamp}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexShrink: 0, color: disabled ? 'text.secondary' : 'primary.main' }}
          >
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {intl.formatMessage({
                id: 'epiChat.assetCard.viewDetails',
                defaultMessage: 'View details',
              })}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 16 }} aria-hidden />
          </Stack>
        </Stack>
      </ButtonBase>
    </Box>
  );
};
